import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { CaretDown, CaretRight, MagicWand, Terminal } from 'phosphor-react';
import { DotFillIcon } from '@primer/octicons-react';
import { Box } from '@primer/react';
import { LogsDrawerProps } from '../types';
import { SystemLogs } from './SystemLogs';
import { ComfyLogs } from './ComfyLogs';
import { AiAssistLogs } from './AiAssistLogs';
import { useParams } from 'react-router-dom';

export interface LogsDrawerRef {
  openSystemLogs: () => void;
}

export const LogsDrawer = forwardRef<LogsDrawerRef, LogsDrawerProps>(
  ({ clusterId, deploymentStatus, comfyUrl }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [panelHeight, setPanelHeight] = useState(0);
    const [logsTab, setLogsTab] = useState('ComfyUI');
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { projectId } = useParams<{ projectId: string }>();

    useEffect(() => {
      return () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, []);

    const toggleLogs = () => {
      if (isExpanded) {
        setPanelHeight(0);
      } else {
        setPanelHeight(350);
      }
      setIsExpanded(!isExpanded);
    };

    const openSystemLogs = () => {
      // First switch to System Logs tab
      setLogsTab('Build Logs');

      // Then expand the drawer if not already expanded
      if (!isExpanded) {
        setPanelHeight(350);
        setIsExpanded(true);
      }
    };

    // Expose methods to parent components
    useImperativeHandle(ref, () => ({
      openSystemLogs,
    }));

    // Auto-switch to System Logs if current tab is disabled
    useEffect(() => {
      const isDeployed = deploymentStatus === 'deployed';
      if (!isDeployed && (logsTab === 'ComfyUI' || logsTab === 'AI Assist (BETA)')) {
        setLogsTab('Build Logs');
      }
    }, [deploymentStatus, logsTab]);

    const tabs = ['ComfyUI', 'Build Logs', 'AI Assist (BETA)'];
    const isDeployed = deploymentStatus === 'deployed';

    return (
      <div style={{ maxHeight: 400 }}>
        <Box
          onClick={toggleLogs}
          className="w-full px-4 py-2 text-sm bg-[#16191D] cursor-pointer border-[#343B45] border-t flex items-center justify-between gap-2"
        >
          <span className="items-center flex gap-3">
            <Terminal size={16} />
            Logs
            {deploymentStatus && deploymentStatus !== 'stopped' && (
              <DotFillIcon size={16} fill="#10f000" />
            )}
          </span>
          {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </Box>
        <Box
          sx={{ height: panelHeight }}
          className="flex flex-col border-t border-[#343B45] overflow-hidden"
        >
          <Box className="flex flex-col h-full overflow-x-hidden bg-[#22262C]">
            <Box className="flex gap-1 p-1 items-center border-b border-[#343B45]">
              {tabs.map((tab) => {
                const isTabDisabled =
                  !isDeployed && (tab === 'ComfyUI' || tab === 'AI Assist (BETA)');

                return (
                  <button
                    key={tab}
                    className={`text-white text-xs flex items-center gap-1
                     rounded-2xl px-3 py-1 transition-opacity ${
                       logsTab === tab
                         ? 'bg-[#16191D] border-solid border border-[#343B45]'
                         : 'bg-[#23272e]'
                     } ${
                      isTabDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-opacity-80'
                    }`}
                    onClick={() => !isTabDisabled && setLogsTab(tab)}
                    disabled={isTabDisabled}
                  >
                    {tab === 'AI Assist (BETA)' && <MagicWand />}
                    {tab}
                  </button>
                );
              })}
            </Box>
            <Box className="block h-full">
              {logsTab === 'Build Logs' ? (
                <SystemLogs
                  isAccordionOpen={isExpanded}
                  clusterId={clusterId}
                  deploymentStatus={deploymentStatus}
                />
              ) : logsTab === 'ComfyUI' ? (
                <ComfyLogs comfyUrl={comfyUrl} isAccordionOpen={isExpanded} />
              ) : (
                <AiAssistLogs
                  clusterId={clusterId}
                  isAccordionOpen={isExpanded}
                  projectId={projectId}
                  deploymentStatus={deploymentStatus}
                />
              )}
            </Box>
          </Box>
        </Box>
      </div>
    );
  }
);
